/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useQuery, type QueryHookOptions } from '@apollo/client';
import { isNil, isNotNil } from 'es-toolkit';

import {
  convertRawDataToPlan,
  getIsPlanInaccessible,
} from '@fai/friendli-suite/features/products/serverless-endpoints/plan';
import {
  ServerlessFreeCreditDocument,
  TeamServerlessEndpointsPlanDocument,
  type TeamServerlessEndpointsPlanQuery,
  type TeamServerlessEndpointsPlanQueryVariables,
} from '@fai/friendli-suite/generated/client/graphql';

export const useServerlessEndpointsTeamPlan = (
  teamId: string,
  queryOptions?: QueryHookOptions<
    TeamServerlessEndpointsPlanQuery,
    TeamServerlessEndpointsPlanQueryVariables
  >
) => {
  const {
    data: rawData,
    loading,
    error,
  } = useQuery(TeamServerlessEndpointsPlanDocument, {
    variables: { teamId },
    fetchPolicy: 'network-only',
    ...queryOptions,
  });

  const userTeamData = rawData?.clientSession?.user.team;
  const productServerlessData = userTeamData?.team.serverlessSubplan;

  const teamRole = userTeamData?.role;
  const isProductAccessible = userTeamData?.productAccess?.serverlessEndpoints;
  const productPlan = productServerlessData?.tier;
  const productState = productServerlessData?.state;

  const plan = convertRawDataToPlan({
    teamRole,
    isProductAccessible,
    productPlan,
    productState,
  });

  const isPlanInaccessible = getIsPlanInaccessible(plan);

  return {
    plan,
    isPlanInaccessible,
    loading,
    error,
  };
};

export const useServerlessFreeCredit = (teamId: string) => {
  const { data: _data, loading } = useQuery(ServerlessFreeCreditDocument, {
    variables: { teamId },
    fetchPolicy: 'network-only',
    pollInterval: 60000, // Changing of free credit is cannot be tracked, so polling is used in this query.
  });

  const _used = _data?.serverlessSubplan?.freeCreditUsage?.used;
  const _total = _data?.serverlessSubplan?.freeCreditUsage?.total;

  const used = isNil(_used) || isNaN(parseFloat(_used)) ? null : parseFloat(_used);
  const total = isNil(_total) || isNaN(parseFloat(_total)) ? null : parseFloat(_total);
  const remaining = isNotNil(used) && isNotNil(total) ? total - used : null;
  const isFreeCreditExist = isNotNil(remaining) && remaining > 0;

  return { used, total, remaining, isFreeCreditExist, loading };
};
